import React, {useState, useEffect} from 'react'
import { useSearchParams } from 'react-router-dom';
import './FantasyCreate.css';

import Header from '../../sections/Header/Header';
import Footer from '../../sections/Footer/Footer';
import SEO from '../../components/SEO/SEO';

import { submitFantasy } from '../../../services/fantasyService';
import { getPublicModels } from '../../../services/modelService';
import { getModelPhoto } from '../../../util/ModelPhotos';

const modelIdentity = (model) => model?.uuid || model?.id || `${model?.citySlug || model?.city || ''}:${model?.slug || model?.name || ''}`;
const modelValue = (model) => model?.slug || model?.uuid || model?.id || model?.name || '';
const uniqueModels = (models) => models.filter((model, index, items) => items.findIndex(item => modelIdentity(item) === modelIdentity(model)) === index);

const FantasyCreate = () => {

    const [searchParams] = useSearchParams()
    const [form, setForm] = useState({
        title:'', email:'', phone:'', city:'', date:'', duration:'', 
        talent:'', talentCount:'1',
        hairColour:'', ageRange:'', bustSize:'', height:'', bodyType:'', ethnicity:'',
        setting:'', scenario:'',
        transportation:'', hotel:'',
        dining:'', wardrobe:'', 
        specialRequests:'',
        budget:''
    })
    const [submitted, setSubmitted] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [selectedModels, setSelectedModels] = useState([])
    const [models, setModels] = useState([])
    const [modelsLoading, setModelsLoading] = useState(true)
    const [modelsError, setModelsError] = useState('')

    useEffect(() => {
        let mounted = true
        getPublicModels()
            .then((items) => {
                if (mounted) setModels(uniqueModels(Array.isArray(items) ? items : []))
            })
            .catch((loadError) => {
                if (mounted) setModelsError(loadError.message || 'We could not load the model profiles.')
            })
            .finally(() => {
                if (mounted) setModelsLoading(false)
            })
        return () => { mounted = false }
    }, [])

    useEffect(() => {
        const modelParam = searchParams.get('model')
        if (modelParam) {
            setForm(prev => ({...prev, talent: 'specific'}))
            setSelectedModels([modelParam])
        }
    }, [searchParams])

    const handleChange = (field, value) => {
        setForm(prev => ({...prev, [field]: value}))
        if (error) setError('')
    }

    const handleSubmit = async () => {
        const contact = form.email.trim()
        const missing = []
        if (!form.title.trim()) missing.push('your name')
        if (!contact) missing.push('an email address or phone number')
        if (!form.city) missing.push('a city')
        if (!form.date) missing.push('a date')
        if (missing.length) {
            setError(`Please provide ${missing.join(', ')}.`)
            return
        }

        setSubmitting(true)
        setError('')
        const payload = {
            ...form,
            title: form.title.trim(),
            city: form.city.trim(),
            date: form.date,
            selectedModels
        }
        delete payload.email
        if (contact.includes('@')) {
            payload.email = contact.toLowerCase()
        } else {
            payload.phone = contact
        }

        try {
            await submitFantasy(payload)
            setSubmitted(true)
        } catch (requestError) {
            setError(requestError.message || 'We could not submit your fantasy request. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            <SEO title="Request Custom Fantasy • Rome & Milan" description="Create your bespoke adult fantasy experience. Choose elite talent, luxury settings, and arrangements in Rome or Milan. Concierge-managed from start to finish." />
            <Header />
            <section className="section-header fantasy-create-page">
                <div className="view-container">
                    <h1>Fantasy Request</h1>
                    <p className="fc-intro">
                        Design your perfect experience. Define every detail - the setting, talent, 
                        scenario, and arrangements. Our concierge team will bring it to life.
                    </p>

                    {submitted ? (
                        <div className="fc-success">
                            <p>Your fantasy has been submitted. Our concierge team will reach out within 24 hours to confirm details and arrange everything.</p>
                        </div>
                    ) : (
                        <div className="fc-form">
                            {error && <p className="fc-error" role="alert">{error}</p>}

                            <div className="fc-section-label">Overview</div>

                            <div className="fc-field">
                                <label>Contact Name</label>
                                <input type="text" value={form.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="Your name" />
                            </div>
                            <div className="fc-field">
                                <label>Email or Phone</label>
                                <input type="text" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="email@example.com or +39..." />
                            </div>
                            <div className="fc-row">
                                <div className="fc-field">
                                    <label>City</label>
                                    <select value={form.city} onChange={(e) => handleChange('city', e.target.value)}>
                                        <option value="">Select</option>
                                        {['Rome','Milan','Florence','Naples','Venice','Turin','Bologna','Verona'].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="fc-field">
                                    <label>Date</label>
                                    <input type="date" value={form.date} onChange={(e) => handleChange('date', e.target.value)} />
                                </div>
                            </div>
                            <div className="fc-field">
                                <label>Duration</label>
                                <select value={form.duration} onChange={(e) => handleChange('duration', e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="evening">Evening (4-6 hours)</option>
                                    <option value="overnight">Overnight</option>
                                    <option value="full-day">Full Day</option>
                                    <option value="weekend">Weekend (2-3 days)</option>
                                    <option value="extended">Extended (4+ days)</option>
                                </select>
                            </div>

                            <div className="fc-section-label">Talent</div>

                            <div className="fc-row">
                                <div className="fc-field">
                                    <label>Talent Preference</label>
                                    <select value={form.talent} onChange={(e) => { handleChange('talent', e.target.value); setSelectedModels([]); }}>
                                        <option value="">Select</option>
                                        <option value="specific">Specific Model (specify below)</option>
                                        <option value="matched">Matched by Concierge</option>
                                        <option value="open">Open to Suggestions</option>
                                    </select>
                                </div>
                                <div className="fc-field">
                                    <label>Number of Talent</label>
                                    <select value={form.talentCount} onChange={(e) => { handleChange('talentCount', e.target.value); setSelectedModels(prev => prev.slice(0, parseInt(e.target.value) || 5)); }}>
                                        {['1','2','3','4','5+'].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                            </div>

                            {form.talent === 'specific' && (
                                <div className="fc-field">
                                    <label>Select Models (max {form.talentCount})</label>
                                    {modelsLoading && <p className="fc-model-feedback" role="status">Loading model profiles...</p>}
                                    {!modelsLoading && modelsError && <p className="fc-model-feedback fc-model-error" role="alert">{modelsError}</p>}
                                    {!modelsLoading && !modelsError && models.length === 0 && <p className="fc-model-feedback">No model profiles are currently available.</p>}
                                    {!modelsLoading && !modelsError && models.length > 0 && <div className="fc-model-checklist">
                                        {models.map(model => {
                                            const value = modelValue(model)
                                            const max = form.talentCount === '5+' ? Infinity : parseInt(form.talentCount)
                                            const checked = selectedModels.includes(value)
                                            const disabled = !checked && selectedModels.length >= max
                                            return (
                                                <label key={modelIdentity(model)} className={`fc-model-item${disabled ? ' disabled' : ''}${checked ? ' checked' : ''}`} onClick={(e) => { e.preventDefault(); if (!disabled || checked) setSelectedModels(prev => checked ? prev.filter(selected => selected !== value) : [...prev, value]) }}>
                                                    <input type="checkbox" checked={checked} disabled={disabled} readOnly />
                                                    <span>{model.name || value}</span>
                                                </label>
                                            )
                                        })}
                                    </div>}
                                    {selectedModels.length > 0 && (
                                        <div className="fc-selected-photos">
                                            {models.filter(model => selectedModels.includes(modelValue(model))).map(model => (
                                                <div className="fc-selected-photo" key={modelIdentity(model)}>
                                                    <img src={getModelPhoto(model)} alt={model.name || modelValue(model)} />
                                                    <span className="fc-selected-name">{model.name || modelValue(model)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {form.talent === 'matched' && (() => {
                                const multi = form.talentCount !== '1'
                                return (
                                <div className="fc-preferences">
                                    <div className="fc-row">
                                        <div className="fc-field">
                                            <label>Hair Colour</label>
                                            <select value={form.hairColour} onChange={(e) => handleChange('hairColour', e.target.value)}>
                                                <option value="">No preference</option>
                                                <option value="blonde">Blonde</option>
                                                <option value="brunette">Brunette</option>
                                                <option value="black">Black</option>
                                                <option value="red">Red</option>
                                                {multi && <option value="blonde-brunette">Blonde & Brunette</option>}
                                                {multi && <option value="mix">Mix of all</option>}
                                            </select>
                                        </div>
                                        <div className="fc-field">
                                            <label>Age Range</label>
                                            <select value={form.ageRange} onChange={(e) => handleChange('ageRange', e.target.value)}>
                                                <option value="">No preference</option>
                                                <option value="18-22">18–22</option>
                                                <option value="23-27">23–27</option>
                                                <option value="28-33">28–33</option>
                                                <option value="34+">34+</option>
                                                {multi && <option value="mixed-ages">Mix of ages</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="fc-row">
                                        <div className="fc-field">
                                            <label>Bust Size</label>
                                            <select value={form.bustSize} onChange={(e) => handleChange('bustSize', e.target.value)}>
                                                <option value="">No preference</option>
                                                <option value="small">Small</option>
                                                <option value="medium">Medium</option>
                                                <option value="large">Large</option>
                                                {multi && <option value="mix">Mix of sizes</option>}
                                            </select>
                                        </div>
                                        <div className="fc-field">
                                            <label>Height</label>
                                            <select value={form.height} onChange={(e) => handleChange('height', e.target.value)}>
                                                <option value="">No preference</option>
                                                <option value="petite">Petite (under 160cm)</option>
                                                <option value="average">Average (160–170cm)</option>
                                                <option value="tall">Tall (170cm+)</option>
                                                {multi && <option value="mix">Mix of heights</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="fc-row">
                                        <div className="fc-field">
                                            <label>Body Type</label>
                                            <select value={form.bodyType} onChange={(e) => handleChange('bodyType', e.target.value)}>
                                                <option value="">No preference</option>
                                                <option value="slim">Slim</option>
                                                <option value="athletic">Athletic</option>
                                                <option value="curvy">Curvy</option>
                                                {multi && <option value="mix">Mix of types</option>}
                                            </select>
                                        </div>
                                        <div className="fc-field">
                                            <label>Ethnicity</label>
                                            <select value={form.ethnicity} onChange={(e) => handleChange('ethnicity', e.target.value)}>
                                                <option value="">No preference</option>
                                                <option value="european">European</option>
                                                <option value="latina">Latina</option>
                                                <option value="asian">Asian</option>
                                                <option value="african">African</option>
                                                <option value="mixed">Mixed</option>
                                                {multi && <option value="mix-all">Mix of ethnicities</option>}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                )
                            })()}

                            <div className="fc-section-label">Setting & Scenario</div>

                            <div className="fc-field">
                                <label>Setting</label>
                                <select value={form.setting} onChange={(e) => handleChange('setting', e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="hotel-suite">Hotel Suite</option>
                                    <option value="private-villa">Private Villa</option>
                                    <option value="yacht">Yacht</option>
                                    <option value="restaurant">Private Dining</option>
                                    <option value="nightclub">Nightclub / Lounge</option>
                                    <option value="outdoor">Outdoor / Nature</option>
                                    <option value="custom">Custom Location</option>
                                </select>
                            </div>
                            <div className="fc-field">
                                <label>Scenario Description</label>
                                <textarea value={form.scenario} onChange={(e) => handleChange('scenario', e.target.value)} rows="4" placeholder="Describe the experience you envision..." />
                            </div>

                            <div className="fc-section-label">Logistics</div>

                            <div className="fc-field">
                                <label>Transportation</label>
                                <select value={form.transportation} onChange={(e) => handleChange('transportation', e.target.value)}>
                                    <option value="">None needed</option>
                                    <option value="airport">Airport Transfer</option>
                                    <option value="chauffeur">Private Chauffeur</option>
                                    <option value="luxury-car">Luxury Car Hire</option>
                                    <option value="multi-city">Multi-City Transfer</option>
                                </select>
                            </div>
                            <div className="fc-field">
                                <label>Hotel / Accommodation</label>
                                <select value={form.hotel} onChange={(e) => handleChange('hotel', e.target.value)}>
                                    <option value="">Not needed</option>
                                    <option value="5-star">5-Star Hotel Suite</option>
                                    <option value="penthouse">Penthouse</option>
                                    <option value="villa">Private Villa</option>
                                    <option value="boutique">Boutique Hotel</option>
                                </select>
                            </div>

                            <div className="fc-section-label">Arrangements</div>

                            <div className="fc-row">
                                <div className="fc-field">
                                    <label>Dining</label>
                                    <select value={form.dining} onChange={(e) => handleChange('dining', e.target.value)}>
                                        <option value="">None</option>
                                        <option value="fine-dining">Fine Dining Reservation</option>
                                        <option value="private-chef">Private Chef</option>
                                        <option value="in-room">In-Room Dining</option>
                                    </select>
                                </div>
                                <div className="fc-field">
                                    <label>Wardrobe & Styling</label>
                                    <select value={form.wardrobe} onChange={(e) => handleChange('wardrobe', e.target.value)}>
                                        <option value="">None</option>
                                        <option value="lingerie">Lingerie Selection</option>
                                        <option value="formal">Formal Attire</option>
                                        <option value="themed">Themed / Costume</option>
                                        <option value="custom">Custom Styling</option>
                                    </select>
                                </div>
                            </div>
                            <div className="fc-field">
                                <label>Special Requests</label>
                                <textarea value={form.specialRequests} onChange={(e) => handleChange('specialRequests', e.target.value)} rows="3" placeholder="Anything else we should know..." />
                            </div>

                            <div className="fc-section-label">Budget</div>

                            <div className="fc-field">
                                <label>Budget Range</label>
                                <select value={form.budget} onChange={(e) => handleChange('budget', e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="1-3k">€1,000 – €3,000</option>
                                    <option value="3-5k">€3,000 – €5,000</option>
                                    <option value="5-10k">€5,000 – €10,000</option>
                                    <option value="10-25k">€10,000 – €25,000</option>
                                    <option value="25k+">€25,000+</option>
                                </select>
                                <p className="fc-disclaimer">Budget should not include talent compensation. Payment to talent is arranged directly between you and them, separate from our services.</p>
                            </div>

                            <button type="button" className="fc-submit" onClick={handleSubmit} disabled={submitting}>
                                {submitting ? 'Submitting...' : 'Submit Fantasy'}
                            </button>
                        </div>
                    )}
                </div>
            </section>
            <Footer />
        </>
    )
}

export default FantasyCreate
